import { useState, useEffect, useRef } from "react";
import axios from "axios";

/**
 * Champ « Ville » avec autocomplétion VERROUILLÉE À LA LISTE (mode recherche,
 * comme les avocats).
 * ---------------------------------------------------------------------------
 * Référentiel `Static.dbo.villes` chargé une seule fois puis mis en cache au
 * niveau module (partagé entre toutes les instances de l'application).
 *
 * Règle « liste seulement » : on ne peut enregistrer QUE :
 *   - une ville présente dans la liste (sélection ou saisie exacte), OU
 *   - une valeur vide (le champ peut être effacé), OU
 *   - la valeur existante inchangée (préserve les données legacy hors-liste).
 * Toute saisie libre non trouvée est annulée à la perte de focus (retour à la
 * dernière valeur validée). La valeur du parent (`value`) n'est mise à jour
 * qu'au moment d'un commit (sélection / effacement / correspondance exacte).
 * ---------------------------------------------------------------------------
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _villesCache = null;
let _villesPromise = null;

const loadVilles = () => {
  if (_villesCache) return Promise.resolve(_villesCache);
  if (!_villesPromise) {
    _villesPromise = axios
      .get(`${BACKEND_URL}/api/reference/villes`)
      .then((res) => {
        _villesCache = Array.isArray(res.data) ? res.data : [];
        return _villesCache;
      })
      .catch(() => {
        _villesPromise = null; // autorise une nouvelle tentative
        return [];
      });
  }
  return _villesPromise;
};

const VilleAutocomplete = ({
  value = "",
  onChange,
  onSelect,
  className = "win-input",
  style,
  disabled = false,
  maxLength = 40,
  testId,
  placeholder = "Ville…",
}) => {
  const [liste, setListe] = useState(_villesCache || []);
  const [search, setSearch] = useState(value || "");
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    loadVilles().then((v) => { if (alive) setListe(v); });
    return () => { alive = false; if (blurTimer.current) clearTimeout(blurTimer.current); };
  }, []);

  // Resynchronise le texte affiché quand la valeur validée change de l'extérieur
  // (chargement dossier, reset formulaire, sélection depuis un autre flux).
  useEffect(() => { setSearch(value || ""); }, [value]);

  const commit = (v) => { onChange && onChange(v.nom); onSelect && onSelect(v); setSearch(v.nom); };

  // Validation à la perte de focus : verrou « liste seulement ».
  const handleBlur = () => {
    setOpen(false);
    const s = (search || "").trim();
    const cur = (value || "").trim();
    if (s === cur) return;                 // inchangé → on préserve (y compris legacy hors-liste)
    if (s === "") { onChange && onChange(""); return; }  // effacement autorisé
    const exact = liste.find((v) => (v.nom || "").toLowerCase() === s.toLowerCase());
    if (exact) { commit(exact); }
    else { setSearch(value || ""); }       // saisie non trouvée → annulée
  };

  const q = (search || "").trim().toLowerCase();
  const filtered = q
    ? liste.filter((v) => (v.nom || "").toLowerCase().includes(q))
    : liste;

  return (
    <div style={{ position: "relative" }}>
      <input
        className={className}
        style={style}
        value={search}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => !disabled && setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(handleBlur, 150); }}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoComplete="off"
        data-testid={testId}
      />
      {open && !disabled && filtered.length > 0 && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0,
            background: "#fff", border: "1px solid #888", zIndex: 3000,
            maxHeight: 220, overflowY: "auto", fontSize: 13, minWidth: 220,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
          data-testid={testId ? `${testId}-menu` : undefined}
        >
          {filtered.slice(0, 20).map((v, i) => (
            <div
              key={`${v.nom}-${v.cp}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); commit(v); setOpen(false); }}
              style={{ padding: "4px 8px", cursor: "pointer", borderBottom: "1px solid #eee", color: "#111" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#dde7f5")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              <strong>{v.nom}</strong>
              {v.regionadm ? <span style={{ color: "#666" }}> — {v.regionadm}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VilleAutocomplete;
